/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import { type IconData } from '../../types';
import { Icon, type IconProps } from '../../canvasx/Icon';
import { getIcon } from '../../utils/icons';
import { type IconType } from '../../objects/IconsHelper';
import { DEFAULT_FONT_SIZE } from '../../wireframes/WireframeBase';
import { Rect } from '@lib/canvasx/components/Rect';

export const getIconSvg = (
  iconData: IconData | string
): IconType | undefined => {
  let icon;
  if (typeof iconData === 'string') {
    icon = getIcon(`fas/${iconData}`);
  } else {
    icon = getIcon(`${iconData.prefix}/${iconData.iconName}`);
  }
  if (icon?.icon) {
    const [width, height, , , d] = icon.icon;
    return { width, height, d: d as string, fillRule: 'evenodd' };
  }
};

const iconScale = ({ fontSize = DEFAULT_FONT_SIZE, icon }) => {
  return fontSize / (icon.height ?? 1);
};

export type SvgIconProps = {
  iconData: IconData | string;
  padding?: SpacingType;
  opacity?: number;
  size?: number;
} & Partial<IconProps>;

export const SvgIcon: CanvaSXFC<SvgIconProps> = function (props) {
  const {
    x = 0,
    y = 0,
    size = DEFAULT_FONT_SIZE,
    iconData,
    padding,
    iconFill = 'black',
    opacity = 1,
  } = props;

  const iconSvg = getIconSvg(iconData);
  if (iconSvg) {
    const scale = iconScale({ fontSize: size, icon: iconSvg });
    return (
      <Rect x={x} y={y} width={size} height={size} padding={padding}>
        <Icon
          x={(size - iconSvg.width * scale) / 2}
          icon={iconSvg}
          iconFill={iconFill}
          opacity={opacity}
          scale={scale}
        />
      </Rect>
    );
  }

  // The icon library for this icon implementation may not have all icons from
  // the original implementation's icon library. In this case, do nothing. In
  // that case, just allow the user to set the icon again
  return null;
};
